// Pestana Resumen: KPIs del periodo, situacion actual, comparativa por
// sucursal, alertas y los cortes de venta del periodo.
// Recibe todo ya calculado: cargar y agregar es cosa de la pantalla.

import React from 'react';
import { AlertTriangle, ArrowRight, BarChart3, Boxes, CalendarClock, Clock, CreditCard, DollarSign, LayoutList, MapPin, Package, Scale, ShoppingCart, Target, TrendingDown, TrendingUp, Truck, Users, Wallet, XCircle } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, LineChart, Pie, PieChart, Tooltip, XAxis, YAxis } from 'recharts';
import Chart from '../../components/Chart';
import { PIE_COLORS, formatDateLong, formatDateShort, formatInt, formatMoney, formatPercent, margenColorClass, safeName } from './formato';
import { AlertCard, DataTable, EmptyState, KpiCard, SectionCard } from './componentes';
import { leyendaPuntoEquilibrio } from '../../utils/puntoEquilibrio';

// ---------- Evolución del punto de equilibrio ----------
// Color de la barra de ventas según cómo quedó el mes contra su equilibrio.
const COLOR_EQUILIBRIO = {
  cumplida: '#10B981',      // emerald: las ventas cubrieron los fijos
  pendiente: '#F59E0B',     // amber: no llegó
  no_alcanzable: '#F43F5E', // rose: costos variables ≥ ventas
  sin_dato: '#CBD5E1',      // slate: sin fijos cargados o sin ventas
};
const colorBarraEquilibrio = (d) => {
  if (d.estado === 'no_alcanzable') return COLOR_EQUILIBRIO.no_alcanzable;
  if (d.estado !== 'ok') return COLOR_EQUILIBRIO.sin_dato;
  return d.cumplida ? COLOR_EQUILIBRIO.cumplida : COLOR_EQUILIBRIO.pendiente;
};

function TooltipEquilibrio({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  let estado;
  if (d.estado === 'ok') {
    estado = d.cumplida ? 'Meta cubierta' : `Faltaron $ ${formatMoney(d.faltante)}`;
  } else {
    estado = leyendaPuntoEquilibrio(d.estado);
  }
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs shadow-sm">
      <p className="font-black text-slate-800 uppercase">{d.label}{d.enCurso ? ' · en curso' : ''}</p>
      <p className="text-slate-600 mt-1">Ventas: <b>$ {formatMoney(d.ventas)}</b></p>
      <p className="text-slate-600">
        Punto de equilibrio: <b>{d.puntoEquilibrio == null ? '—' : `$ ${formatMoney(d.puntoEquilibrio)}`}</b>
      </p>
      <p className="text-slate-600">Gastos fijos: $ {formatMoney(d.gastosFijos)}</p>
      {d.ventas > 0 && (
        <p className="text-slate-600">Margen de contribución: {formatPercent(d.margenContribucionPct)}</p>
      )}
      <p className="mt-1 font-bold text-slate-700">{estado}</p>
    </div>
  );
}

export default function TabResumen({
  alertas,
  breakEven,
  evolucionEquilibrio,
  comparativaSucursales,
  deltaBeneficio,
  deltaResultado,
  deltaVentas,
  metrics,
  navigate,
  periodLabel,
  proveedoresDeuda,
  range,
  setShowPrevSeries,
  setViewDeudaProveedores,
  setViewTopClientes,
  showPrevSeries,
  snapshot,
  topClientes,
  vencimientosHoy,
  vencimientosProveedores,
  ventasPorDia,
  ventasPorMetodo,
  viewDeudaProveedores,
  viewTopClientes,
}) {
  return (
    <>
            {/* KPIs del PERIODO — respetan el rango de fechas elegido */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <CalendarClock size={14} className="text-slate-400" />
                <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">
                  Del periodo · {periodLabel}
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
              <KpiCard
                icon={DollarSign}
                label="Ventas del periodo"
                value={`$ ${formatMoney(metrics.ventasTotal)}`}
                delta={deltaVentas}
                accent="bg-blue-50 text-blue-600"
                hint={`${formatInt(metrics.ticketsCount)} tickets · ticket prom. $ ${formatMoney(metrics.ticketPromedio)}`}
              />
              <KpiCard
                icon={TrendingUp}
                label="Beneficio bruto"
                value={`$ ${formatMoney(metrics.beneficio)}`}
                delta={deltaBeneficio}
                accent="bg-emerald-50 text-emerald-600"
                hint={`Margen ${formatPercent(metrics.margenPct)}`}
              />
              <KpiCard
                icon={Scale}
                label="Resultado neto"
                value={`$ ${formatMoney(metrics.resultadoNeto)}`}
                delta={deltaResultado}
                accent={
                  metrics.resultadoNeto < 0
                    ? 'bg-rose-50 text-rose-600'
                    : 'bg-emerald-50 text-emerald-600'
                }
                hint={
                  metrics.tieneGastos
                    ? `Bruto $ ${formatMoney(metrics.beneficio)} - gastos $ ${formatMoney(metrics.gastosPeriodo)}`
                    : 'Sin gastos registrados en el periodo: igual al bruto'
                }
              />
              <KpiCard
                icon={Target}
                label="Meta de equilibrio"
                value={
                  breakEven.puntoEquilibrio == null
                    ? (breakEven.estado === 'no_alcanzable' ? 'No alcanzable' : 'Sin datos')
                    : `$ ${formatMoney(breakEven.puntoEquilibrio)}`
                }
                accent={
                  breakEven.estado === 'no_alcanzable'
                    ? 'bg-rose-50 text-rose-600'
                    : breakEven.cumplida ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'
                }
                hint={
                  breakEven.estado === 'ok'
                    ? breakEven.cumplida
                      ? `Cubierta · margen de contribución ${formatPercent(breakEven.margenContribucionPct)}`
                      : `Faltan $ ${formatMoney(breakEven.faltante)} · fijos $ ${formatMoney(metrics.gastosFijos)}`
                    : breakEven.estado === 'sin_ventas' || metrics.tieneGastos
                      ? leyendaPuntoEquilibrio(breakEven.estado)
                      : 'Carga gastos del periodo para calcularla'
                }
              />
              </div>
            </section>

            {/* Situacion ACTUAL — estos valores NO dependen del rango de fechas:
                salen de saldos de hoy y de la RPC del Dashboard. Iban mezclados
                con los del periodo y se leian como magnitudes del rango. */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <Clock size={14} className="text-slate-400" />
                <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">
                  Situacion actual
                </h2>
                <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                  no depende del periodo
                </span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              <KpiCard
                icon={Wallet}
                label="A cobrar"
                value={`$ ${formatMoney(metrics.aCobrar)}`}
                accent="bg-amber-50 text-amber-600"
                hint={`${alertas.deudores.length} clientes con deuda`}
              />
              <KpiCard
                icon={CreditCard}
                label="A pagar"
                value={`$ ${formatMoney(metrics.aPagar)}`}
                accent="bg-rose-50 text-rose-600"
                hint={`Saldo actual con proveedores`}
              />
              <KpiCard
                icon={Wallet}
                label="Saldo financiero"
                value={`$ ${formatMoney(snapshot.saldoCaja)}`}
                accent={snapshot.bancosNegativos > 0 ? 'bg-rose-50 text-rose-600' : 'bg-sky-50 text-sky-600'}
                hint={
                  snapshot.bancosNegativos > 0
                    ? `${snapshot.bancosNegativos} cuenta(s) en negativo`
                    : 'Liquidez actual en bancos'
                }
              />
              </div>
            </section>

            {/* Comparativa por sucursal: solo en consolidado y con mas de una. */}
            {comparativaSucursales.length > 1 && (
              <SectionCard
                title="Comparativa por sucursal"
                icon={MapPin}
                action={
                  <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                    {periodLabel}
                  </span>
                }
              >
                <DataTable
                  emptyMessage="Sin datos por sucursal"
                  rows={comparativaSucursales}
                  columns={[
                    { key: 'nombre', label: 'Sucursal', cellClass: () => 'font-black text-slate-800 truncate max-w-[200px]' },
                    { key: 'ventas', label: 'Ventas', align: 'right', cellClass: () => 'font-black text-slate-900', render: (r) => `$ ${formatMoney(r.ventas)}` },
                    { key: 'tickets', label: 'Tickets', align: 'right', cellClass: () => 'font-bold text-slate-600', render: (r) => formatInt(r.tickets) },
                    { key: 'ticketPromedio', label: 'Ticket prom.', align: 'right', cellClass: () => 'text-slate-500', render: (r) => `$ ${formatMoney(r.ticketPromedio)}` },
                    { key: 'margenPct', label: 'Margen', align: 'right', cellClass: (r) => `font-black ${margenColorClass(r.margenPct)}`, render: (r) => formatPercent(r.margenPct) },
                    {
                      key: 'resultadoNeto',
                      label: 'Resultado neto',
                      align: 'right',
                      cellClass: (r) => `font-black ${r.resultadoNeto < 0 ? 'text-rose-600' : 'text-emerald-600'}`,
                      render: (r) => `$ ${formatMoney(r.resultadoNeto)}`,
                    },
                  ]}
                />
                {/* Sin gastos cargados el "resultado neto" ES el bruto. Callarlo
                    haria leer como rentabilidad final algo que no descuenta
                    nada — la org multi-sucursal tipica no carga gastos. */}
                {!metrics.tieneGastos && (
                  <p className="mt-3 text-[11px] text-slate-500">
                    No hay gastos cargados en el periodo: el resultado neto coincide con el
                    beneficio bruto y todavia no descuenta costos fijos.
                  </p>
                )}
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-2 text-[11px]">
                  <span className="font-black uppercase tracking-widest text-slate-500">Consolidado</span>
                  <span className="text-slate-600">
                    <strong className="font-black text-slate-900">$ {formatMoney(metrics.ventasTotal)}</strong>
                    {' · '}
                    {formatInt(metrics.ticketsCount)} tickets
                    {' · '}
                    neto{' '}
                    <strong className={`font-black ${metrics.resultadoNeto < 0 ? 'text-rose-600' : 'text-emerald-600'}`}>
                      $ {formatMoney(metrics.resultadoNeto)}
                    </strong>
                  </span>
                </div>
              </SectionCard>
            )}

            {/* Alertas */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <AlertTriangle size={14} className="text-amber-500" />
                <h2 className="text-[11px] font-black uppercase tracking-widest text-slate-700">
                  Alertas inteligentes
                </h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                <AlertCard
                  icon={XCircle}
                  title="Productos sin stock"
                  count={alertas.sinStock.length}
                  detail={
                    alertas.sinStock.length
                      ? alertas.sinStock
                          .slice(0, 3)
                          .map((p) => safeName(p.name))
                          .join(' · ')
                      : 'Todos los productos tienen stock disponible.'
                  }
                  tone="rose"
                  onClick={alertas.sinStock.length ? () => navigate('/sistema/inventario') : null}
                />
                <AlertCard
                  icon={Package}
                  title="Bajo el mínimo"
                  count={alertas.bajoStock.length}
                  detail={
                    alertas.bajoStock.length
                      ? `Top: ${alertas.bajoStock
                          .slice(0, 3)
                          .map((p) => `${safeName(p.name)} (${p.stock}/${p.min})`)
                          .join(' · ')}`
                      : 'Ningún producto en alerta de reposición.'
                  }
                  tone="amber"
                  onClick={alertas.bajoStock.length ? () => navigate('/sistema/inventario') : null}
                />
                <AlertCard
                  icon={Users}
                  title="Clientes con deuda"
                  count={alertas.deudores.length}
                  detail={
                    alertas.deudores.length
                      ? `Mayor saldo: ${alertas.deudores[0]?.full_name || '-'} ($ ${formatMoney(
                          alertas.deudores[0]?._balance,
                        )})`
                      : 'Sin deudores activos.'
                  }
                  tone="sky"
                  onClick={alertas.deudores.length ? () => navigate('/sistema/reportes-ctas-ctes') : null}
                />
                <AlertCard
                  icon={Boxes}
                  title="Sin ventas en el periodo"
                  count={alertas.sinVenta.length}
                  detail={
                    range.days < 7
                      ? `Con ${periodLabel} el dato es ruido: mirá 30 días o más para que signifique algo.`
                      : alertas.sinVenta.length
                        ? `Con stock y sin vender en ${periodLabel}: $ ${formatMoney(alertas.sinVentaValor)} de costo inmovilizado.`
                        : `Todo lo que tenía stock vendió al menos una unidad en ${periodLabel}.`
                  }
                  tone="slate"
                  onClick={alertas.sinVenta.length ? () => navigate('/sistema/inventario') : null}
                />
                <AlertCard
                  icon={ShoppingCart}
                  title="Pedidos web pendientes"
                  count={snapshot.pedidosWeb}
                  detail={
                    snapshot.pedidosWeb
                      ? 'Pedidos nuevos esperando armado o despacho.'
                      : 'No hay pedidos web sin atender.'
                  }
                  tone="sky"
                  onClick={snapshot.pedidosWeb ? () => navigate('/sistema/pedidos-web') : null}
                />
                <AlertCard
                  icon={TrendingDown}
                  title="Baja rentabilidad"
                  count={snapshot.bajaRentabilidad}
                  detail={
                    snapshot.bajaRentabilidad
                      ? 'Productos con margen real por debajo del objetivo.'
                      : 'Todos los productos están sobre el margen objetivo.'
                  }
                  tone="rose"
                  onClick={snapshot.bajaRentabilidad ? () => navigate('/sistema/rentabilidad-ventas') : null}
                />
                <AlertCard
                  icon={Clock}
                  title="Vencimientos hoy"
                  count={vencimientosHoy}
                  detail={
                    vencimientosHoy
                      ? `${snapshot.proyIngresosHoy} cobro(s) · ${snapshot.proyEgresosHoy} pago(s) proyectados para hoy.`
                      : 'No hay movimientos proyectados para hoy.'
                  }
                  tone="amber"
                  onClick={vencimientosHoy ? () => navigate('/sistema/flujo-fondos') : null}
                />
              </div>
            </section>

            {/* Charts */}
            <section className="grid grid-cols-1 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] gap-3">
              <SectionCard
                title="Ventas por día"
                icon={BarChart3}
                action={
                  <button
                    type="button"
                    onClick={() => setShowPrevSeries((v) => !v)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest border transition-colors ${
                      showPrevSeries
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-500 border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    Comparar
                  </button>
                }
              >
                {ventasPorDia.every((d) => d.ventas === 0) ? (
                  <EmptyState message="Sin ventas en el periodo" />
                ) : (
                  // minWidth/minHeight 0 evita el warning de recharts cuando el
                  // grid/flex padre no completó layout al montar el chart.
                  <div style={{ width: '100%', height: 240, minWidth: 0, minHeight: 0 }}>
                    <Chart>
                      <LineChart data={ventasPorDia} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis
                          dataKey="label"
                          stroke="#94A3B8"
                          fontSize={11}
                          interval={Math.floor(ventasPorDia.length / 12) || 0}
                        />
                        <YAxis
                          stroke="#94A3B8"
                          fontSize={11}
                          tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
                        />
                        <Tooltip
                          formatter={(value, name, item) => {
                            const money = `$ ${formatMoney(value)}`;
                            // El punto del tramo anterior cae en otra fecha que
                            // la del eje: sin decir cual, el numero no se puede
                            // interpretar.
                            if (name === 'prevVentas') {
                              const f = item?.payload?.prevFecha;
                              return [money, f ? `Anterior (${formatDateShort(f)})` : 'Anterior'];
                            }
                            return [money, 'Periodo'];
                          }}
                          labelStyle={{ fontWeight: 'bold' }}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        {showPrevSeries && (
                          <Line
                            type="monotone"
                            dataKey="prevVentas"
                            stroke="#94A3B8"
                            strokeWidth={2}
                            strokeDasharray="4 4"
                            dot={false}
                            activeDot={{ r: 4 }}
                            connectNulls={false}
                          />
                        )}
                        <Line
                          type="monotone"
                          dataKey="ventas"
                          stroke="#51A7FD"
                          strokeWidth={2.5}
                          dot={{ r: 3 }}
                          activeDot={{ r: 5 }}
                        />
                      </LineChart>
                    </Chart>
                  </div>
                )}
              </SectionCard>

              <SectionCard title="Métodos de pago" icon={CreditCard}>
                {ventasPorMetodo.length === 0 ? (
                  <EmptyState message="Sin métodos registrados" />
                ) : (
                  <div style={{ width: '100%', height: 240, minWidth: 0, minHeight: 0 }}>
                    <Chart>
                      <PieChart>
                        <Pie
                          data={ventasPorMetodo}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          innerRadius={45}
                          outerRadius={80}
                          paddingAngle={2}
                        >
                          {ventasPorMetodo.map((entry, index) => (
                            <Cell key={entry.name} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => `$ ${formatMoney(value)}`}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        <Legend
                          verticalAlign="bottom"
                          height={36}
                          iconSize={10}
                          formatter={(value) => <span style={{ fontSize: 11, color: '#475569' }}>{value}</span>}
                        />
                      </PieChart>
                    </Chart>
                  </div>
                )}
              </SectionCard>
            </section>

            {/* Evolución del punto de equilibrio — últimos 12 meses, no depende del rango */}
            {evolucionEquilibrio && (
              <section>
                <SectionCard
                  title="Evolución del punto de equilibrio"
                  icon={Scale}
                  action={
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                      Últimos 12 meses
                    </span>
                  }
                >
                  {evolucionEquilibrio.loading ? (
                    <EmptyState message="Calculando…" />
                  ) : evolucionEquilibrio.error ? (
                    <EmptyState message="No se pudo cargar la evolución mensual" />
                  ) : evolucionEquilibrio.serie.every((d) => d.ventas === 0 && d.gastosFijos === 0) ? (
                    <EmptyState message="Sin ventas ni gastos en los últimos 12 meses" />
                  ) : (
                    <>
                      <div style={{ width: '100%', height: 260, minWidth: 0, minHeight: 0 }}>
                        <Chart>
                          <ComposedChart data={evolucionEquilibrio.serie} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                            <XAxis dataKey="label" stroke="#94A3B8" fontSize={11} />
                            <YAxis
                              stroke="#94A3B8"
                              fontSize={11}
                              tickFormatter={(v) => (v >= 1000000 ? `${(v / 1000000).toFixed(1)}M` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : `${v}`)}
                            />
                            <Tooltip content={<TooltipEquilibrio />} />
                            <Bar dataKey="ventas" name="Ventas" radius={[6, 6, 0, 0]} maxBarSize={36}>
                              {evolucionEquilibrio.serie.map((d) => (
                                <Cell key={d.mes} fill={colorBarraEquilibrio(d)} fillOpacity={d.enCurso ? 0.55 : 1} />
                              ))}
                            </Bar>
                            <Line
                              type="monotone"
                              dataKey="puntoEquilibrio"
                              name="Punto de equilibrio"
                              stroke="#7C3AED"
                              strokeWidth={2.5}
                              dot={{ r: 3 }}
                              activeDot={{ r: 5 }}
                              connectNulls={false}
                            />
                          </ComposedChart>
                        </Chart>
                      </div>
                      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] font-bold text-slate-500">
                        <span className="flex items-center gap-1.5"><i className="inline-block h-0.5 w-4 rounded" style={{ background: '#7C3AED' }} /> Punto de equilibrio</span>
                        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_EQUILIBRIO.cumplida }} /> Ventas que cubrieron la meta</span>
                        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_EQUILIBRIO.pendiente }} /> No llegaron</span>
                        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_EQUILIBRIO.no_alcanzable }} /> No alcanzable</span>
                        <span className="flex items-center gap-1.5"><i className="inline-block h-2.5 w-2.5 rounded-sm" style={{ background: COLOR_EQUILIBRIO.sin_dato }} /> Sin gastos fijos cargados</span>
                      </div>
                      <p className="mt-2 text-[10px] text-slate-400 leading-relaxed">
                        Mes a mes: gastos fijos ÷ % de margen de contribución
                        {evolucionEquilibrio.incluyeVariables ? ' (ventas − CMV − gastos variables)' : ' (ventas − CMV; los gastos variables no se restan por configuración de la empresa)'}.
                        No depende del rango de fechas elegido; el mes en curso está incompleto (barra más clara).
                        {evolucionEquilibrio.serie.some((d) => d.ventas > 0 && d.gastosFijos === 0)
                          ? ' Los meses en gris no tienen gastos fijos cargados: sin ellos no hay equilibrio que calcular.'
                          : ''}
                      </p>
                    </>
                  )}
                </SectionCard>
              </section>
            )}

            {/* Top clientes (los reportes de producto viven en la pestaña Productos) */}
            <section className="grid grid-cols-1 gap-3">
              {/* ── Top clientes ── */}
              <SectionCard
                title="Top clientes"
                icon={Users}
                action={
                  topClientes.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setViewTopClientes((v) => v === 'chart' ? 'table' : 'chart')}
                        title={viewTopClientes === 'chart' ? 'Ver tabla' : 'Ver gráfico'}
                        className={`inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                          viewTopClientes === 'table'
                            ? 'border-blue-200 bg-blue-50 text-blue-600'
                            : 'border-slate-200 bg-white text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        {viewTopClientes === 'chart' ? <LayoutList size={12} /> : <BarChart3 size={12} />}
                        {viewTopClientes === 'chart' ? 'Tabla' : 'Gráfico'}
                      </button>
                      <button
                        type="button"
                        onClick={() => navigate('/sistema/clientes')}
                        className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-slate-700 flex items-center gap-1"
                      >
                        Ver más <ArrowRight size={11} />
                      </button>
                    </div>
                  )
                }
              >
                {topClientes.length === 0 ? (
                  <EmptyState icon={Users} message="Sin ventas a clientes registrados" />
                ) : viewTopClientes === 'chart' ? (
                  <div style={{ width: '100%', height: Math.max(220, topClientes.length * 34), minWidth: 0 }}>
                    <Chart>
                      <BarChart
                        data={[...topClientes].reverse().map((c) => ({ name: c.nombre.length > 18 ? c.nombre.slice(0, 18) + '…' : c.nombre, total: c.total, compras: c.count }))}
                        layout="vertical"
                        margin={{ top: 0, right: 16, bottom: 0, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" stroke="#94A3B8" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}`} />
                        <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={110} tick={{ fill: '#475569' }} />
                        <Tooltip
                          formatter={(value, name) => [name === 'total' ? `$ ${formatMoney(value)}` : value, name === 'total' ? 'Facturación' : 'Compras']}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        <Bar dataKey="total" fill="#A88BFF" radius={[0, 4, 4, 0]} maxBarSize={20} />
                      </BarChart>
                    </Chart>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                          <th className="pb-2">Cliente</th>
                          <th className="pb-2 text-right">Compras</th>
                          <th className="pb-2 text-right">Facturación</th>
                          <th className="pb-2 text-right">Saldo</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {topClientes.map((c) => {
                          const saldoColor = c.balance > 0.01 ? 'text-rose-600' : c.balance < -0.01 ? 'text-emerald-600' : 'text-slate-400';
                          return (
                            <tr key={c.id}>
                              <td className="py-2 font-black text-slate-800 truncate max-w-[200px]">{c.nombre}</td>
                              <td className="py-2 text-right font-bold text-slate-600">{c.count}</td>
                              <td className="py-2 text-right font-black text-slate-900">$ {formatMoney(c.total)}</td>
                              <td className={`py-2 text-right font-black ${saldoColor}`}>$ {formatMoney(c.balance)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </SectionCard>
            </section>

            {/* Mayores deudas (la atención de stock vive en la pestaña Productos) */}
            <section className="grid grid-cols-1 gap-3">
              <SectionCard title="Mayores deudas" icon={Wallet}>
                {alertas.deudores.length === 0 ? (
                  <EmptyState icon={Wallet} message="Ningún cliente con deuda" />
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {alertas.deudores.slice(0, 8).map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => navigate(`/sistema/clientes-detalle/${c.id}`)}
                        className="w-full flex items-center justify-between bg-slate-50 hover:bg-slate-100 transition-colors rounded-xl px-3 py-2 text-left"
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">
                            {c.full_name || 'Sin nombre'}
                          </p>
                          {c.doc_number && (
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                              {c.doc_number}
                            </p>
                          )}
                        </div>
                        <span className="text-xs font-black text-rose-600">$ {formatMoney(c._balance)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </SectionCard>
            </section>

            {/* Proveedores y vencimientos de deudas */}
            <section className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <SectionCard
                title="Deuda con proveedores"
                icon={Truck}
                action={
                  proveedoresDeuda.length > 0 && (
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setViewDeudaProveedores((v) => v === 'chart' ? 'table' : 'chart')}
                        title={viewDeudaProveedores === 'chart' ? 'Ver tabla' : 'Ver gráfico'}
                        className={`inline-flex h-7 items-center gap-1 rounded-lg border px-2 text-[10px] font-black uppercase tracking-widest transition-colors ${
                          viewDeudaProveedores === 'table'
                            ? 'border-blue-200 bg-blue-50 text-blue-600'
                            : 'border-slate-200 bg-white text-slate-400 hover:text-slate-700'
                        }`}
                      >
                        {viewDeudaProveedores === 'chart' ? <LayoutList size={12} /> : <BarChart3 size={12} />}
                        {viewDeudaProveedores === 'chart' ? 'Tabla' : 'Gráfico'}
                      </button>
                      <span className="text-[10px] font-black uppercase tracking-widest text-rose-600">
                        $ {formatMoney(proveedoresDeuda.reduce((acc, p) => acc + p._balance, 0))}
                      </span>
                    </div>
                  )
                }
              >
                {proveedoresDeuda.length === 0 ? (
                  <EmptyState icon={Truck} message="Ningún proveedor con deuda" />
                ) : viewDeudaProveedores === 'chart' ? (
                  <div style={{ width: '100%', height: Math.max(200, proveedoresDeuda.length * 36), minWidth: 0 }}>
                    <Chart>
                      <BarChart
                        data={[...proveedoresDeuda].reverse().map((p) => ({
                          name: (p.razon_social || 'Sin nombre').length > 18 ? (p.razon_social || 'Sin nombre').slice(0, 18) + '…' : (p.razon_social || 'Sin nombre'),
                          deuda: p._balance,
                        }))}
                        layout="vertical"
                        margin={{ top: 0, right: 16, bottom: 0, left: 4 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                        <XAxis type="number" stroke="#94A3B8" fontSize={10} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : `${v}`} />
                        <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={10} width={120} tick={{ fill: '#475569' }} />
                        <Tooltip
                          formatter={(value) => [`$ ${formatMoney(value)}`, 'Deuda']}
                          contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0', fontSize: 12 }}
                        />
                        <Bar dataKey="deuda" fill="#F05A57" radius={[0, 4, 4, 0]} maxBarSize={22} />
                      </BarChart>
                    </Chart>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[360px] overflow-y-auto">
                    {proveedoresDeuda.map((p) => (
                      <div key={p.id} className="w-full flex items-center justify-between bg-slate-50 rounded-xl px-3 py-2">
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">{p.razon_social || 'Sin razón social'}</p>
                          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Saldo a pagar</p>
                        </div>
                        <span className="text-xs font-black text-rose-600">$ {formatMoney(p._balance)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>

              <SectionCard
                title="Próximos vencimientos"
                icon={CalendarClock}
                action={
                  vencimientosProveedores.length > 0 && (
                    <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      {vencimientosProveedores.filter((v) => v.overdue).length} vencidas
                    </span>
                  )
                }
              >
                {vencimientosProveedores.length === 0 ? (
                  <EmptyState icon={CalendarClock} message="Sin compras pendientes de pago" />
                ) : (
                  <div className="space-y-2 max-h-[360px] overflow-y-auto">
                    {vencimientosProveedores.slice(0, 20).map((v) => (
                      <div
                        key={v.id}
                        className={`w-full flex items-center justify-between rounded-xl px-3 py-2 border ${
                          v.overdue
                            ? 'bg-rose-50/50 border-rose-100'
                            : 'bg-slate-50 border-transparent'
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="text-xs font-black text-slate-800 truncate">{v.proveedor}</p>
                          <p
                            className={`text-[10px] font-bold uppercase tracking-widest ${
                              v.overdue ? 'text-rose-600' : 'text-slate-400'
                            }`}
                          >
                            {v.overdue ? 'Vencida' : 'Vence'} {formatDateLong(v.fechaVencimiento)}
                            {v.condicion ? ` · ${v.condicion}` : ''}
                          </p>
                        </div>
                        <span
                          className={`text-xs font-black ${
                            v.overdue ? 'text-rose-600' : 'text-slate-900'
                          }`}
                        >
                          $ {formatMoney(v.monto)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </SectionCard>
            </section>
    </>
  );
}
